import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
  type Header,
  type Row,
  type RowData,
} from "@tanstack/react-table";
import { useTranslation } from "react-i18next";

/**
 * Per-column metadata. Currently only `sortable` (TM-035ae774): when a
 * column sets `meta: { sortable: true }` the `<th>` renders a clickable
 * header with a sort-direction indicator and forwards clicks to
 * `onSortChange`. Registries that don't set it keep the plain header — so
 * employees/clients are untouched.
 */
declare module "@tanstack/react-table" {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    sortable?: boolean;
  }
}

export interface DataTableProps<T> {
  data: T[];
  columns: ColumnDef<T, unknown>[];
  /** testid for the <table> element, useful in registry tests. */
  testId?: string;
  /** Optional click handler on a row (for inline navigation/edit). */
  onRowClick?: (row: T) => void;
  /** "Loading" / "Empty" markers; both default to common.* keys. */
  isLoading?: boolean;
  emptyMessage?: string;
  /**
   * Server-side sort state (TM-035ae774). `sort` is the active column id and
   * `order` its direction; `onSortChange` is fired with the clicked column
   * id when a `meta.sortable` header is activated. The parent owns the state
   * and re-fetches — this component never sorts client-side.
   */
  sort?: string;
  order?: "asc" | "desc";
  onSortChange?: (columnId: string) => void;
}

/**
 * Thin TanStack-Table v8 wrapper used by every registry view. Server-side
 * pagination & filtering live in the parent — this component only renders
 * what it receives.
 */
export function DataTable<T>({
  data,
  columns,
  testId,
  onRowClick,
  isLoading,
  emptyMessage,
  sort,
  order,
  onSortChange,
}: DataTableProps<T>) {
  const { t } = useTranslation();
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (isLoading === true) {
    return (
      <p
        className="text-sm text-muted-foreground"
        data-testid="datatable-loading"
      >
        {t("common.loading")}
      </p>
    );
  }
  if (data.length === 0) {
    return (
      <p
        className="text-sm text-muted-foreground"
        data-testid="datatable-empty"
      >
        {emptyMessage ?? t("common.empty")}
      </p>
    );
  }

  return (
    <div className="overflow-x-auto rounded-md border">
      <table className="w-full text-sm" data-testid={testId}>
        <thead className="bg-muted/40">
          {table.getHeaderGroups().map((hg) => (
            <tr key={hg.id}>
              {hg.headers.map((h) => (
                <HeaderCell
                  key={h.id}
                  header={h}
                  sort={sort}
                  order={order}
                  onSortChange={onSortChange}
                />
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <DataRow key={row.id} row={row} onRowClick={onRowClick} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface HeaderCellProps<T> {
  header: Header<T, unknown>;
  sort?: string;
  order?: "asc" | "desc";
  onSortChange?: (columnId: string) => void;
}

/**
 * One `<th>`. When the column opts in via `meta.sortable` AND the parent
 * wired `onSortChange`, the label becomes a button that toggles server-side
 * sort and shows a direction indicator: the active column gets ↑/↓ by
 * `order`, an inactive sortable column gets a muted up/down glyph. Plain
 * columns render exactly as before — no behaviour change for callers that
 * never set the meta flag.
 */
function HeaderCell<T>({
  header,
  sort,
  order,
  onSortChange,
}: HeaderCellProps<T>) {
  const content = header.isPlaceholder
    ? null
    : flexRender(header.column.columnDef.header, header.getContext());

  const sortable =
    header.column.columnDef.meta?.sortable === true &&
    onSortChange !== undefined;

  if (!sortable) {
    return (
      <th className="border-b px-3 py-2 text-left font-medium" scope="col">
        {content}
      </th>
    );
  }

  const isActive = sort === header.column.id;
  return (
    <th
      className="border-b px-3 py-2 text-left font-medium"
      scope="col"
      aria-sort={
        isActive ? (order === "asc" ? "ascending" : "descending") : "none"
      }
    >
      <button
        type="button"
        className="inline-flex items-center gap-1 font-medium hover:text-foreground"
        onClick={() => onSortChange(header.column.id)}
        data-testid={`sort-${header.column.id}`}
      >
        <span>{content}</span>
        <SortIndicator active={isActive} order={order} />
      </button>
    </th>
  );
}

/**
 * Sort-direction glyph. We inline the SVGs (matching the project's existing
 * no-lucide-react convention — see `CitationBlock`) so we don't add a runtime
 * dependency just for three arrows.
 */
function SortIndicator({
  active,
  order,
}: {
  active: boolean;
  order?: "asc" | "desc";
}) {
  if (active) {
    return order === "asc" ? (
      <ArrowUpIcon className="text-foreground" />
    ) : (
      <ArrowDownIcon className="text-foreground" />
    );
  }
  return <ArrowUpDownIcon className="text-muted-foreground/50" />;
}

function ArrowUpIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className={className}
    >
      <path d="m18 15-6-6-6 6" />
    </svg>
  );
}

function ArrowDownIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className={className}
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

function ArrowUpDownIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className={className}
    >
      <path d="m21 16-4 4-4-4" />
      <path d="M17 20V4" />
      <path d="m3 8 4-4 4 4" />
      <path d="M7 4v16" />
    </svg>
  );
}

interface DataRowProps<T> {
  row: Row<T>;
  onRowClick?: (row: T) => void;
}

function DataRow<T>({ row, onRowClick }: DataRowProps<T>) {
  return (
    <tr
      data-testid="datatable-row"
      className={onRowClick ? "cursor-pointer hover:bg-accent/40" : undefined}
      onClick={onRowClick ? () => onRowClick(row.original) : undefined}
    >
      {row.getVisibleCells().map((cell) => (
        <td key={cell.id} className="border-b px-3 py-2 align-top">
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </td>
      ))}
    </tr>
  );
}
